import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { 
  User, Mail, MapPin, Shield, ShieldCheck, Trash2, Plus, X, 
  Clock, CheckCircle, Building2, RefreshCw 
} from "lucide-react";

interface ProviderUser {
  id: string;
  email: string;
  fullName: string;
  clinicName: string | null;
  createdAt: string;
  emailConfirmed: boolean;
  roles: string[];
  locationAccess: {
    user_id: string;
    location_id: string;
    is_primary: boolean;
    locations: {
      name: string;
      city: string;
      state: string;
    };
  }[];
  isPending: boolean;
}

interface Location {
  id: string;
  name: string;
  city: string;
  state: string;
}

export const ProviderUserManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedLocationForUser, setSelectedLocationForUser] = useState<{ userId: string; locationId: string } | null>(null);

  // Fetch users from edge function
  const { data: usersData, isLoading: usersLoading, refetch } = useQuery({
    queryKey: ['provider-users'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('manage-provider-users', {
        body: { action: 'list' },
      });

      if (error) throw error;
      return data as { users: ProviderUser[] };
    },
  });

  // Fetch locations for assignment
  const { data: locations = [] } = useQuery({
    queryKey: ['locations-for-assignment'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('id, name, city, state')
        .order('name');
      
      if (error) throw error;
      return data as Location[];
    },
  });

  const grantRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { data, error } = await supabase.functions.invoke('manage-provider-users', {
        body: { action: 'grant-role', userId, role },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-users'] });
      toast({ title: "Success", description: "Role granted successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to grant role", variant: "destructive" });
    },
  });

  const revokeRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { data, error } = await supabase.functions.invoke('manage-provider-users', {
        body: { action: 'revoke-role', userId, role },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-users'] });
      toast({ title: "Success", description: "Role revoked successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to revoke role", variant: "destructive" });
    },
  });

  const assignLocationMutation = useMutation({
    mutationFn: async ({ userId, locationId }: { userId: string; locationId: string }) => {
      const { data, error } = await supabase.functions.invoke('manage-provider-users', {
        body: { action: 'assign-location', userId, locationId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-users'] });
      setSelectedLocationForUser(null);
      toast({ title: "Success", description: "Location assigned successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to assign location", variant: "destructive" });
    },
  });

  const removeLocationMutation = useMutation({
    mutationFn: async ({ userId, locationId }: { userId: string; locationId: string }) => {
      const { data, error } = await supabase.functions.invoke('manage-provider-users', {
        body: { action: 'remove-location', userId, locationId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-users'] });
      toast({ title: "Success", description: "Location removed successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to remove location", variant: "destructive" });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.functions.invoke('manage-provider-users', {
        body: { action: 'delete-user', userId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-users'] });
      toast({ title: "Success", description: "User deleted successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete user", variant: "destructive" });
    },
  });

  const users = usersData?.users || [];
  const pendingUsers = users.filter(u => u.isPending);
  const approvedUsers = users.filter(u => !u.isPending);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getAvailableLocations = (user: ProviderUser) => {
    const assignedIds = user.locationAccess.map(a => a.location_id);
    return locations.filter(l => !assignedIds.includes(l.id));
  };

  // Find best matching location based on clinic name
  const findMatchingLocation = (clinicName: string | null): Location | null => {
    if (!clinicName || locations.length === 0) return null;
    
    const normalizedClinic = clinicName.toLowerCase().trim();
    
    // Try exact match first
    const exactMatch = locations.find(
      loc => loc.name.toLowerCase().trim() === normalizedClinic
    );
    if (exactMatch) return exactMatch;
    
    // Try partial match (clinic name contains location name or vice versa)
    const partialMatch = locations.find(
      loc => 
        normalizedClinic.includes(loc.name.toLowerCase()) ||
        loc.name.toLowerCase().includes(normalizedClinic)
    );
    if (partialMatch) return partialMatch;
    
    // Try word-based matching (at least 2 words match)
    const clinicWords = normalizedClinic.split(/\s+/).filter(w => w.length > 2);
    for (const loc of locations) {
      const locWords = loc.name.toLowerCase().split(/\s+/).filter(w => w.length > 2);
      const matchingWords = clinicWords.filter(cw => 
        locWords.some(lw => lw.includes(cw) || cw.includes(lw))
      );
      if (matchingWords.length >= 2) return loc;
    }
    
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Pending Approvals */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Pending Provider Requests
              </CardTitle>
              <CardDescription>
                Users who signed up for provider access and need approval
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {usersLoading ? (
            <p className="text-center py-8 text-muted-foreground">Loading users...</p>
          ) : pendingUsers.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No pending requests</p>
          ) : (
            <div className="space-y-4">
              {pendingUsers.map((user) => {
                const suggestedLocation = findMatchingLocation(user.clinicName);
                
                return (
                  <div key={user.id} className="border rounded-lg p-4 bg-amber-50/50 dark:bg-amber-950/20">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{user.fullName || 'No name provided'}</span>
                          {user.emailConfirmed ? (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Verified
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-600 border-amber-600">
                              <Clock className="w-3 h-3 mr-1" />
                              Unverified
                            </Badge>
                          )}
                        </div>
                        
                        {/* Clinic Name Display */}
                        {user.clinicName && (
                          <div className="flex items-center gap-2 text-sm">
                            <Building2 className="w-4 h-4 text-primary" />
                            <span className="font-medium text-primary">{user.clinicName}</span>
                            {suggestedLocation && (
                              <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-xs">
                                Match: {suggestedLocation.name}
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="w-4 h-4" />
                          {user.email}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Signed up {formatDate(user.createdAt)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Quick Assign Button if match found */}
                        {suggestedLocation && (
                          <Button
                            size="sm"
                            onClick={() => assignLocationMutation.mutate({ 
                              userId: user.id, 
                              locationId: suggestedLocation.id 
                            })}
                            disabled={assignLocationMutation.isPending}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Assign to {suggestedLocation.name}
                          </Button>
                        )}
                        
                        <Select
                          onValueChange={(locationId) => {
                            assignLocationMutation.mutate({ userId: user.id, locationId });
                          }}
                        >
                          <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder={suggestedLocation ? "Or pick another..." : "Assign to location..."} />
                          </SelectTrigger>
                          <SelectContent>
                            {locations.map((location) => (
                              <SelectItem key={location.id} value={location.id}>
                                {location.name} - {location.city}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Reject Request?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will delete the user account for {user.email}. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteUserMutation.mutate(user.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete User
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approved Providers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-green-500" />
            Approved Providers
          </CardTitle>
          <CardDescription>
            Users with provider or admin access
          </CardDescription>
        </CardHeader>
        <CardContent>
          {usersLoading ? (
            <p className="text-center py-8 text-muted-foreground">Loading users...</p>
          ) : approvedUsers.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No approved providers yet</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead>Locations</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approvedUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">{user.fullName || 'No name'}</div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {user.roles.includes('admin') && (
                            <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                              <Shield className="w-3 h-3 mr-1" />
                              Admin
                            </Badge>
                          )}
                          {user.roles.includes('provider') && (
                            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                              <User className="w-3 h-3 mr-1" />
                              Provider
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-2">
                          {user.locationAccess.map((access) => (
                            <div key={access.location_id} className="flex items-center gap-2">
                              <Badge variant="outline" className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {access.locations.name}
                                {access.is_primary && (
                                  <span className="text-xs text-primary">(Primary)</span>
                                )}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => removeLocationMutation.mutate({
                                  userId: user.id,
                                  locationId: access.location_id,
                                })}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                          {getAvailableLocations(user).length > 0 && (
                            <Select
                              onValueChange={(locationId) => {
                                assignLocationMutation.mutate({ userId: user.id, locationId });
                              }}
                            >
                              <SelectTrigger className="h-8 w-[180px]">
                                <div className="flex items-center gap-1 text-xs">
                                  <Plus className="w-3 h-3" />
                                  Add location
                                </div>
                              </SelectTrigger>
                              <SelectContent>
                                {getAvailableLocations(user).map((location) => (
                                  <SelectItem key={location.id} value={location.id}>
                                    {location.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {!user.roles.includes('admin') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => grantRoleMutation.mutate({ userId: user.id, role: 'admin' })}
                            >
                              <Shield className="w-3 h-3 mr-1" />
                              Make Admin
                            </Button>
                          )}
                          {user.roles.includes('admin') && user.roles.includes('provider') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => revokeRoleMutation.mutate({ userId: user.id, role: 'admin' })}
                            >
                              Remove Admin
                            </Button>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-destructive">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete User?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete the account for {user.email}. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteUserMutation.mutate(user.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete User
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
